(() => {
  'use strict';

  if (window.SteezlabProduct) {
    window.SteezlabProduct.init?.();
    return;
  }

  const initializedRoots = new WeakSet();
  const initializedCardCarousels = new WeakSet();
  const productControllers = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const transparentPixel = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

  const collect = (scope, selector) => {
    const matches = scope.matches?.(selector) ? [scope] : [];
    return [...matches, ...scope.querySelectorAll(selector)];
  };

  const wrapIndex = (index, length) => ((index % length) + length) % length;

  const mediaPosition = (template, current, total) =>
    (template || 'Media __CURRENT__ of __TOTAL__')
      .replace(/__CURRENT__/g, String(current))
      .replace(/__TOTAL__/g, String(total));

  const pauseSlideMedia = (slide) => {
    slide.querySelectorAll('video').forEach((video) => video.pause());

    slide.querySelectorAll('iframe').forEach((frame) => {
      frame.contentWindow?.postMessage(JSON.stringify({
        event: 'command',
        func: 'pauseVideo',
        args: []
      }), '*');
      frame.contentWindow?.postMessage({ method: 'pause' }, '*');
    });

    slide.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
  };

  class ProductMediaCarousel {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-media-stage]');
      this.slides = [...root.querySelectorAll('[data-media-item]')];
      this.thumbnails = [...root.querySelectorAll('[data-media-thumbnail]')];
      this.dots = [...root.querySelectorAll('[data-media-dot]')];
      this.current = root.querySelector('[data-media-current]');
      this.status = root.querySelector('[data-media-status]');
      this.dialog = root.querySelector('[data-media-dialog]');
      this.dialogImage = root.querySelector('[data-media-dialog-image]');
      this.positionTemplate = root.dataset.mediaPositionTemplate;
      this.suppressClickUntil = 0;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.scrollFrame = null;
      this.scrollTimer = null;
      this.pointerStartX = null;
      this.pointerStartY = null;
      this.pointerId = null;

      if (!this.stage || !this.slides.length) return;

      const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.currentIndex = activeIndex >= 0 ? activeIndex : 0;
      this.root.classList.add('is-enhanced');
      this.hydrateAround(this.currentIndex);
      this.bindEvents();
      this.select(this.currentIndex, { announce: false, scroll: false });
      this.alignToCurrent();
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const previous = event.target.closest('[data-media-previous]');
        const next = event.target.closest('[data-media-next]');
        const thumbnail = event.target.closest('[data-media-thumbnail]');
        const dot = event.target.closest('[data-media-dot]');
        const zoom = event.target.closest('[data-media-zoom]');

        if (previous) {
          event.preventDefault();
          this.go(-1);
          return;
        }
        if (next) {
          event.preventDefault();
          this.go(1);
          return;
        }
        if (thumbnail) {
          event.preventDefault();
          this.selectByMediaId(thumbnail.dataset.mediaId);
          return;
        }
        if (dot) {
          event.preventDefault();
          this.selectByMediaId(dot.dataset.mediaId);
          return;
        }
        if (!zoom) return;

        if (Date.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (this.dialog && typeof this.dialog.showModal === 'function') {
          event.preventDefault();
          this.openZoom(zoom);
        }
      });

      this.root.addEventListener('keydown', (event) => {
        const thumbnail = event.target.closest('[data-media-thumbnail]');
        const dot = event.target.closest('[data-media-dot]');
        const navigation = thumbnail ? this.thumbnails : dot ? this.dots : null;

        if (navigation && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const currentIndex = Math.max(0, navigation.indexOf(event.target.closest('button')));
          let nextIndex = currentIndex;
          if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = navigation.length - 1;
          else nextIndex = wrapIndex(currentIndex + (event.key === 'ArrowRight' ? 1 : -1), navigation.length);
          const control = navigation[nextIndex];
          this.selectByMediaId(control.dataset.mediaId);
          control.focus({ preventScroll: true });
          return;
        }

        if (!this.stage.contains(event.target)) return;
        if (event.target.closest('video, iframe, model-viewer, input, select, textarea, button')) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        if (event.key === 'Home') this.select(0);
        else if (event.key === 'End') this.select(this.slides.length - 1);
        else this.go(event.key === 'ArrowRight' ? 1 : -1);
      });

      this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event), { passive: true });
      this.stage.addEventListener('pointerup', (event) => this.onPointerUp(event), { passive: true });
      this.stage.addEventListener('pointercancel', () => this.resetPointer(), { passive: true });
      this.stage.addEventListener('scroll', () => this.syncFromScroll(), { passive: true });
      this.mobileQuery.addEventListener?.('change', () => {
        if (this.mobileQuery.matches) requestAnimationFrame(() => this.alignToCurrent());
        else this.stage.scrollLeft = 0;
      });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
    }

    hydrateSlide(slide) {
      const image = slide?.querySelector('[data-media-lazy-image]');
      if (!image || image.dataset.mediaHydrated === 'true') return;
      if (image.dataset.mediaSrcset) image.srcset = image.dataset.mediaSrcset;
      if (image.dataset.mediaSrc) image.src = image.dataset.mediaSrc;
      image.dataset.mediaHydrated = 'true';
    }

    dehydrateSlide(slide) {
      const image = slide?.querySelector('[data-media-lazy-image]');
      if (!image || image.dataset.mediaHydrated !== 'true') return;
      image.removeAttribute('srcset');
      image.src = transparentPixel;
      image.dataset.mediaHydrated = 'false';
    }

    hydrateAround(index) {
      if (!this.slides.length) return;
      const keep = new Set([
        wrapIndex(index - 1, this.slides.length),
        wrapIndex(index, this.slides.length),
        wrapIndex(index + 1, this.slides.length)
      ]);

      keep.forEach((slideIndex) => this.hydrateSlide(this.slides[slideIndex]));

      if (this.mobileQuery.matches && this.slides.length > 24) {
        this.slides.forEach((slide, slideIndex) => {
          if (!keep.has(slideIndex)) this.dehydrateSlide(slide);
        });
      }
    }

    onPointerDown(event) {
      if (!this.mobileQuery.matches || event.pointerType === 'mouse') return;
      if (event.target.closest('button, input, select, textarea, video, iframe, model-viewer')) return;
      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
    }

    onPointerUp(event) {
      if (!this.mobileQuery.matches || this.pointerId !== event.pointerId) return;
      const startX = this.pointerStartX;
      const startY = this.pointerStartY;
      this.resetPointer();
      if (startX == null || startY == null) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;

      this.suppressClickUntil = Date.now() + 350;
      this.go(deltaX < 0 ? 1 : -1);
    }

    resetPointer() {
      this.pointerStartX = null;
      this.pointerStartY = null;
      this.pointerId = null;
    }

    syncFromScroll() {
      // Product media no longer uses a 200+ slide native horizontal scroller on phones.
      // Swipe navigation is handled with pointer gestures to keep iOS Safari memory stable.
    }

    scrollToSlide() {
      // Intentionally empty; mobile uses single-slide rendering plus pointer swipe.
    }

    alignToCurrent() {
      if (this.mobileQuery.matches) this.stage.scrollLeft = 0;
    }

    select(index, { announce = true, focusStage = false, scroll = true } = {}) {
      if (!this.slides.length) return null;
      const nextIndex = wrapIndex(index, this.slides.length);
      this.currentIndex = nextIndex;
      this.hydrateAround(nextIndex);

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        if (!active) pauseSlideMedia(slide);
      });

      this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const active = thumbnailIndex === nextIndex;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', String(active));
        thumbnail.tabIndex = active ? 0 : -1;
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === nextIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
        dot.tabIndex = active ? 0 : -1;
      });

      if (this.current) this.current.textContent = String(nextIndex + 1);
      if (this.status && announce) {
        this.status.textContent = mediaPosition(this.positionTemplate, nextIndex + 1, this.slides.length);
      }
      if (scroll && this.mobileQuery.matches) this.scrollToSlide(nextIndex);
      if (focusStage) this.stage.focus({ preventScroll: true });

      return this.slides[nextIndex];
    }

    selectByMediaId(mediaId, options) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      return index >= 0 ? this.select(index, options) : null;
    }

    go(direction) {
      return this.select(this.currentIndex + direction);
    }

    openZoom(trigger) {
      if (!this.dialog || !this.dialogImage) return;
      const image = new Image();
      image.src = trigger.href;
      image.alt = trigger.dataset.zoomAlt || '';
      image.decoding = 'async';
      this.dialogImage.replaceChildren(image);
      if (!this.dialog.open) this.dialog.showModal();
    }
  }

  class ProductCardCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-card-media-viewport]');
      this.slides = [...root.querySelectorAll('[data-card-media-slide]')];
      this.status = root.querySelector('[data-card-media-status]');
      this.positionTemplate = root.dataset.mediaPositionTemplate;
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.suppressClickUntil = 0;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.scrollFrame = null;
      this.scrollTimer = null;

      if (!this.viewport || this.slides.length < 2) return;

      this.root.classList.add('is-enhanced');
      this.bindEvents();
      this.select(this.currentIndex, { announce: false, scroll: false });
      this.alignToCurrent();
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const previous = event.target.closest('[data-card-media-previous]');
        const next = event.target.closest('[data-card-media-next]');
        const link = event.target.closest('a');

        if (previous || next) {
          event.preventDefault();
          this.select(this.currentIndex + (next ? 1 : -1));
          return;
        }

        if (link && Date.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      });

      this.root.addEventListener('keydown', (event) => {
        if (!this.viewport.contains(event.target)) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        let index = this.currentIndex;
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.slides.length - 1;
        else index += event.key === 'ArrowRight' ? 1 : -1;

        const focusLink = Boolean(event.target.closest('a'));
        const activeSlide = this.select(index);
        if (focusLink) activeSlide?.querySelector('a')?.focus({ preventScroll: true });
      });

      this.viewport.addEventListener('scroll', () => this.syncFromScroll(), { passive: true });
      this.mobileQuery.addEventListener?.('change', () => {
        if (this.mobileQuery.matches) requestAnimationFrame(() => this.alignToCurrent());
        else this.viewport.scrollLeft = 0;
      });
    }

    syncFromScroll() {
      if (!this.mobileQuery.matches) return;
      this.suppressClickUntil = Date.now() + 250;
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const nextIndex = this.slides.reduce((closest, slide, index) => {
          const distance = Math.abs(slide.offsetLeft - this.viewport.scrollLeft);
          return distance < closest.distance ? { index, distance } : closest;
        }, { index: this.currentIndex, distance: Number.POSITIVE_INFINITY }).index;
        if (nextIndex !== this.currentIndex) this.select(nextIndex, { announce: false, scroll: false });
      });

      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        if (this.status) {
          this.status.textContent = mediaPosition(this.positionTemplate, this.currentIndex + 1, this.slides.length);
        }
      }, 140);
    }

    scrollToSlide(index, behavior = reducedMotion.matches ? 'auto' : 'smooth') {
      if (!this.mobileQuery.matches) return;
      const slide = this.slides[index];
      if (!slide) return;
      this.viewport.scrollTo({ left: slide.offsetLeft, behavior });
    }

    alignToCurrent() {
      if (!this.mobileQuery.matches) return;
      const slide = this.slides[this.currentIndex];
      if (!slide) return;
      const previousBehavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = slide.offsetLeft;
      requestAnimationFrame(() => {
        this.viewport.style.scrollBehavior = previousBehavior;
      });
    }

    select(index, { announce = true, scroll = true } = {}) {
      const nextIndex = wrapIndex(index, this.slides.length);
      this.currentIndex = nextIndex;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        const link = slide.querySelector('a');
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        if (link) link.tabIndex = active ? 0 : -1;
      });

      if (this.status && announce) {
        this.status.textContent = mediaPosition(this.positionTemplate, nextIndex + 1, this.slides.length);
      }
      if (scroll && this.mobileQuery.matches) this.scrollToSlide(nextIndex);

      return this.slides[nextIndex];
    }
  }

  class ProductModelSelector {
    constructor(root) {
      this.root = root;
      this.trigger = root.querySelector('[data-product-selector-trigger]');
      this.value = root.querySelector('[data-selected-option-value]');
      this.panel = root.querySelector('[data-product-selector-panel]');
      this.closeButton = root.querySelector('[data-product-selector-close]');
      this.search = root.querySelector('[data-product-selector-search]');
      this.list = root.querySelector('[data-product-selector-list]');
      this.empty = root.querySelector('[data-product-selector-empty]');
      this.options = [...root.querySelectorAll('[data-product-selector-option]')];
      this.groups = [...root.querySelectorAll('[data-product-selector-group]')];
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.prompt = this.value?.textContent.trim() || '';
      this.isOpen = false;

      if (!this.trigger || !this.panel || !this.list) return;

      this.handleDocumentPointer = (event) => {
        if (!this.isOpen || this.mobileQuery.matches || this.root.contains(event.target)) return;
        this.close({ restoreFocus: false });
      };
      this.handleDocumentFocus = (event) => {
        if (!this.isOpen || this.mobileQuery.matches || this.root.contains(event.target)) return;
        this.close({ restoreFocus: false });
      };
      this.handleModeChange = () => this.syncMode();

      this.root.classList.add('is-enhanced');
      this.bindEvents();
      this.refresh();
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-product-selector-trigger]');
        const close = event.target.closest('[data-product-selector-close], [data-product-selector-backdrop]');
        const option = event.target.closest('[data-product-selector-option]');

        if (trigger) {
          event.preventDefault();
          this.isOpen ? this.close() : this.open({ focusTarget: 'search' });
          return;
        }
        if (close) {
          event.preventDefault();
          this.close();
          return;
        }
        if (option) {
          event.preventDefault();
          this.selectOption(option);
        }
      });

      this.root.addEventListener('keydown', (event) => this.handleKeydown(event));
      this.search?.addEventListener('input', () => this.filter(this.search.value));
      document.addEventListener('pointerdown', this.handleDocumentPointer, true);
      document.addEventListener('focusin', this.handleDocumentFocus);
      this.mobileQuery.addEventListener?.('change', this.handleModeChange);
    }

    open({ focusTarget = 'search' } = {}) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.previouslyFocused = document.activeElement;
      this.panel.hidden = false;
      this.root.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.syncMode();
      this.refresh();

      requestAnimationFrame(() => {
        if (focusTarget === 'first') this.focusBoundary('first');
        else if (focusTarget === 'last') this.focusBoundary('last');
        else if (focusTarget === 'selected') this.focusSelectedOrFirst();
        else (this.search || this.closeButton)?.focus({ preventScroll: true });
      });
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.panel.hidden = true;
      this.root.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.removeAttribute('aria-modal');
      if (this.search) this.search.value = '';
      this.filter('');
      if (!document.querySelector('[data-product-selector].is-open')) {
        document.body.classList.remove('is-product-model-selector-open');
      }
      if (restoreFocus) this.trigger.focus({ preventScroll: true });
    }

    syncMode() {
      if (!this.isOpen) return;
      if (this.mobileQuery.matches) {
        this.panel.setAttribute('aria-modal', 'true');
        document.body.classList.add('is-product-model-selector-open');
      } else {
        this.panel.removeAttribute('aria-modal');
        document.body.classList.remove('is-product-model-selector-open');
      }
    }

    filter(rawQuery) {
      const query = rawQuery.trim().toLocaleLowerCase();
      let visibleCount = 0;

      this.options.forEach((option) => {
        const matches = !query || (option.dataset.searchText || '').includes(query);
        option.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      this.groups.forEach((group) => {
        group.hidden = !this.options.some((option) => option.closest('[data-product-selector-group]') === group && !option.hidden);
      });

      if (this.empty) this.empty.hidden = visibleCount !== 0;
      const selected = this.selectedOption();
      const selectedIsVisible = selected
        && !selected.hidden
        && !selected.closest('[data-product-selector-group]')?.hidden
        && !this.isDisabled(selected);
      this.setRovingOption(selectedIsVisible ? selected : this.visibleEnabledOptions()[0]);
    }

    refresh() {
      let selected = null;
      this.options.forEach((option) => {
        const input = option.querySelector('[data-option-input]');
        const isSelected = Boolean(input?.checked);
        const isDisabled = Boolean(input?.disabled);
        if (isSelected) selected = option;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-selected', String(isSelected));
        option.setAttribute('aria-disabled', String(isDisabled));
        const status = option.querySelector('[data-product-selector-option-status]');
        if (status) status.hidden = !isDisabled;
      });

      if (this.value) this.value.textContent = selected?.querySelector('[data-option-input]')?.value || this.prompt;
      this.setRovingOption(selected && !this.isDisabled(selected) ? selected : this.visibleEnabledOptions()[0]);
    }

    selectOption(option) {
      const input = option?.querySelector('[data-option-input]');
      if (!input || input.disabled || this.isDisabled(option)) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.refresh();
      this.close();
    }

    selectedOption() {
      return this.options.find((option) => option.querySelector('[data-option-input]')?.checked) || null;
    }

    isDisabled(option) {
      return option.getAttribute('aria-disabled') === 'true' || Boolean(option.querySelector('[data-option-input]')?.disabled);
    }

    visibleEnabledOptions() {
      return this.options.filter((option) =>
        !option.hidden
        && !option.closest('[data-product-selector-group]')?.hidden
        && !this.isDisabled(option)
      );
    }

    setRovingOption(target) {
      this.options.forEach((option) => {
        option.tabIndex = option === target ? 0 : -1;
        option.classList.toggle('is-active-option', option === target);
      });
    }

    focusOption(option) {
      if (!option) return;
      this.setRovingOption(option);
      option.focus({ preventScroll: true });
      option.scrollIntoView({ block: 'nearest' });
    }

    focusBoundary(boundary) {
      const options = this.visibleEnabledOptions();
      this.focusOption(boundary === 'last' ? options.at(-1) : options[0]);
    }

    focusSelectedOrFirst() {
      const selected = this.selectedOption();
      this.focusOption(selected && !this.isDisabled(selected) && !selected.hidden ? selected : this.visibleEnabledOptions()[0]);
    }

    focusRelative(option, direction) {
      const options = this.visibleEnabledOptions();
      if (!options.length) return;
      const currentIndex = Math.max(0, options.indexOf(option));
      this.focusOption(options[wrapIndex(currentIndex + direction, options.length)]);
    }

    trapFocus(event) {
      const focusable = [
        this.closeButton,
        this.search,
        ...this.visibleEnabledOptions()
      ].filter(Boolean);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    handleKeydown(event) {
      const onTrigger = event.target === this.trigger;
      if (!this.isOpen && onTrigger) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.open({ focusTarget: 'search' });
        } else if (event.key === 'ArrowDown' || event.key === 'Home') {
          event.preventDefault();
          this.open({ focusTarget: 'first' });
        } else if (event.key === 'ArrowUp' || event.key === 'End') {
          event.preventDefault();
          this.open({ focusTarget: 'last' });
        }
        return;
      }

      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab' && this.mobileQuery.matches) {
        this.trapFocus(event);
        return;
      }

      if (event.target === this.search) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.focusBoundary('first');
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.focusBoundary('last');
        }
        return;
      }

      const option = event.target.closest('[data-product-selector-option]');
      if (!option) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.selectOption(option);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.focusRelative(option, 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.focusRelative(option, -1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.focusBoundary('first');
      } else if (event.key === 'End') {
        event.preventDefault();
        this.focusBoundary('last');
      }
    }

    destroy() {
      this.close({ restoreFocus: false });
      document.removeEventListener('pointerdown', this.handleDocumentPointer, true);
      document.removeEventListener('focusin', this.handleDocumentFocus);
      this.mobileQuery.removeEventListener?.('change', this.handleModeChange);
    }
  }

  class ProductController {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-product-form]');
      this.variantInput = this.form?.querySelector('[data-variant-id]');
      this.optionGroups = [...root.querySelectorAll('[data-product-option]')]
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
      this.variants = this.readJSON('[data-product-variants]', []);
      this.strings = this.readJSON('[data-product-strings]', {});
      this.phoneOptionPosition = Number(root.dataset.phoneOptionPosition || 0);
      this.phoneOptionGroup = this.optionGroups.find((group) =>
        Number(group.dataset.optionPosition) === this.phoneOptionPosition
      );
      // The visible Shopify-selected variant is purchasable immediately.
      // Changing PHONE MODEL still updates the real Shopify variant normally.
      this.requiresPhoneSelection = false;
      this.phoneSelectionConfirmed = true;
      this.dynamicCheckout = this.form?.querySelector('.product-form__dynamic-checkout');

      this.bindEvents();
      this.initializePhoneSelection();
      this.initializePhoneSelector();
      this.initializeSearchFields();
      this.initializeMedia();
      this.initializeStickyButton();
      this.updateVariant({ updateUrl: false, announce: false });
    }

    readJSON(selector, fallback) {
      const element = this.root.querySelector(selector);
      if (!element) return fallback;
      try {
        return JSON.parse(element.textContent);
      } catch (_error) {
        return fallback;
      }
    }

    bindEvents() {
      this.root.addEventListener('change', (event) => {
        if (!event.target.matches('[data-option-input], [data-option-select]')) return;
        if (this.phoneOptionGroup?.contains(event.target)) this.confirmPhoneSelection();
        const group = event.target.closest('[data-product-option]');
        const value = event.target.value;
        const selectedLabel = group?.querySelector('[data-selected-option-value]');
        if (selectedLabel) selectedLabel.textContent = value;
        this.updateVariant({ updateUrl: true, announce: true });
      });

      this.root.addEventListener('click', (event) => {
        const stickySubmit = event.target.closest('[data-sticky-submit]');
        if (!stickySubmit || !this.form) return;
        const primarySubmit = this.form.querySelector('[data-product-submit]');
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit(primarySubmit || undefined);
        } else {
          primarySubmit?.click();
        }
      });
    }

    initializePhoneSelection() {
      if (!this.requiresPhoneSelection) return;

      this.root.classList.add('is-phone-selection-required');
      this.phoneOptionGroup.classList.add('is-explicit-selection-required');

      const phoneOutput = this.root.querySelector('[data-setup-phone]');
      const stickyOption = this.root.querySelector('[data-sticky-option]');
      if (phoneOutput) phoneOutput.textContent = '';
      if (stickyOption) stickyOption.textContent = '';

      const prompt = this.phoneOptionGroup.querySelector('[data-phone-selection-prompt]') || document.createElement('p');
      if (!prompt.isConnected) {
        prompt.className = 'product-option__selection-prompt';
        prompt.dataset.phoneSelectionPrompt = '';
        prompt.setAttribute('role', 'status');
        prompt.textContent = this.strings.selectPhone || 'Select your phone to continue.';
        this.phoneOptionGroup.append(prompt);
      }
      this.phoneSelectionPrompt = prompt;

      if (this.dynamicCheckout) {
        this.dynamicCheckout.dataset.phoneSelectionHidden = 'true';
        this.dynamicCheckout.hidden = true;
      }
    }

    initializePhoneSelector() {
      if (!this.phoneOptionGroup?.matches('[data-product-selector]')) return;
      this.phoneSelector = new ProductModelSelector(this.phoneOptionGroup);
    }

    confirmPhoneSelection() {
      if (!this.requiresPhoneSelection || this.phoneSelectionConfirmed) return;
      this.phoneSelectionConfirmed = true;
      this.root.classList.remove('is-phone-selection-required');
      this.phoneOptionGroup?.classList.remove('is-explicit-selection-required');
      if (this.phoneSelectionPrompt) this.phoneSelectionPrompt.hidden = true;
      if (this.dynamicCheckout?.dataset.phoneSelectionHidden === 'true') {
        this.dynamicCheckout.hidden = false;
        delete this.dynamicCheckout.dataset.phoneSelectionHidden;
      }
      this.updateSetup(this.selectedOptions(), this.currentVariant);
    }

    initializeSearchFields() {
      this.root.querySelectorAll('[data-option-search]').forEach((search) => {
        search.addEventListener('input', () => {
          const query = search.value.trim().toLocaleLowerCase();
          const group = search.closest('[data-product-option]');
          const labels = [...(group?.querySelectorAll('[data-option-value-label]') || [])];
          let visibleCount = 0;

          labels.forEach((label) => {
            const matches = !query || (label.dataset.searchText || '').includes(query);
            label.hidden = !matches;
            if (matches) visibleCount += 1;
          });

          const noResults = group?.querySelector('[data-option-no-results]');
          if (noResults) noResults.hidden = visibleCount !== 0;
        });
      });
    }

    initializeMedia() {
      const gallery = this.root.querySelector('[data-media-carousel]');
      if (gallery) this.mediaCarousel = new ProductMediaCarousel(gallery);
    }

    initializeStickyButton() {
      this.sticky = this.root.querySelector('[data-sticky-add-to-cart]');
      const submit = this.form?.querySelector('[data-product-submit]');
      if (!this.sticky || !submit || !('IntersectionObserver' in window)) return;

      const mobileQuery = window.matchMedia('(max-width: 749px)');
      this.submitObserver = new IntersectionObserver((entries) => {
        const primaryVisible = entries[0]?.isIntersecting;
        const showSticky = mobileQuery.matches && !primaryVisible;
        this.sticky.hidden = !showSticky;
        this.sticky.classList.toggle('is-visible', showSticky);
      }, { threshold: 0.15 });
      this.submitObserver.observe(submit);

      mobileQuery.addEventListener?.('change', (event) => {
        if (!event.matches) {
          this.sticky.hidden = true;
          this.sticky.classList.remove('is-visible');
        }
      });
    }

    selectedOptions() {
      return this.optionGroups.map((group) => {
        const selected = group.querySelector('[data-option-select]') || group.querySelector('[data-option-input]:checked');
        return selected?.value || '';
      });
    }

    updateVariant({ updateUrl, announce }) {
      const options = this.selectedOptions();
      const variant = this.optionGroups.length === 0
        ? this.variants.find((candidate) => String(candidate.id) === String(this.root.dataset.selectedVariantId)) || this.variants[0]
        : this.variants.find((candidate) =>
          candidate.options.every((option, index) => option === options[index])
        );

      this.currentVariant = variant || null;
      this.updateOptionAvailability(options);
      this.updateVariantInput(variant);
      this.updatePrice(variant);
      this.updateAvailability(variant, announce);
      this.updateSetup(options, variant);
      this.updateMediaForVariant(variant);
      this.updateSku(variant);

      if (variant && updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.root.dispatchEvent(new CustomEvent('steezlab:variant:change', {
        bubbles: true,
        detail: { variant, options }
      }));
    }

    updateOptionAvailability(selectedOptions) {
      this.optionGroups.forEach((group, groupIndex) => {
        const position = Number(group.dataset.optionPosition) - 1;
        const isProductSelector = group.matches('[data-product-selector]');

        group.querySelectorAll('[data-option-value-label]').forEach((label) => {
          const input = label.querySelector('[data-option-input]');
          if (!input) return;
          const possible = this.variants.some((variant) => {
            if (!variant.available || variant.options[position] !== input.value) return false;
            if (isProductSelector) {
              return variant.options.every((value, index) =>
                index === position || !selectedOptions[index] || value === selectedOptions[index]
              );
            }
            return variant.options.slice(0, groupIndex).every((value, index) =>
              !selectedOptions[index] || value === selectedOptions[index]
            );
          });
          label.classList.toggle('is-unavailable', !possible);
          label.classList.toggle('is-disabled', !possible);
          label.classList.toggle('is-selected', input.checked);
          input.setAttribute('aria-disabled', String(!possible));
          if (isProductSelector) {
            input.disabled = !possible;
            label.setAttribute('aria-disabled', String(!possible));
            const status = label.querySelector('[data-product-selector-option-status]');
            if (status) status.hidden = possible;
          }
        });

        const select = group.querySelector('[data-option-select]');
        if (select) {
          [...select.options].forEach((option) => {
            option.disabled = !this.variants.some((variant) => {
              if (!variant.available || variant.options[position] !== option.value) return false;
              return variant.options.slice(0, groupIndex).every((value, index) =>
                !selectedOptions[index] || value === selectedOptions[index]
              );
            });
          });
        }
      });
      this.phoneSelector?.refresh();
    }

    updateVariantInput(variant) {
      if (!this.variantInput) return;
      if (variant) {
        this.variantInput.value = variant.id;
        this.variantInput.disabled = false;
      } else {
        this.variantInput.value = '';
        this.variantInput.disabled = true;
      }
      this.root.dataset.selectedVariantId = variant?.id || '';
    }

    updatePrice(variant) {
      const current = this.root.querySelector('[data-price-current]');
      const compare = this.root.querySelector('[data-price-compare]');
      const price = this.root.querySelector('[data-price]');
      const stickyPrice = this.root.querySelector('[data-sticky-price]');
      const unitPrice = this.root.querySelector('[data-unit-price]');
      if (!variant) return;

      if (current) current.innerHTML = variant.price;
      if (stickyPrice) stickyPrice.innerHTML = variant.price;

      const onSale = Number(variant.compareAtPriceAmount) > Number(variant.priceAmount);
      if (compare) {
        compare.hidden = !onSale;
        compare.innerHTML = onSale ? variant.compareAtPrice : '';
      }
      price?.classList.toggle('price--on-sale', onSale);
      price?.classList.toggle('price--sold-out', !variant.available);

      if (unitPrice) {
        unitPrice.hidden = !variant.unitPrice;
        if (variant.unitPrice) {
          const value = unitPrice.querySelector('[data-unit-price-value]');
          const referenceValue = unitPrice.querySelector('[data-unit-reference-value]');
          const referenceUnit = unitPrice.querySelector('[data-unit-reference-unit]');
          if (value) value.innerHTML = variant.unitPrice;
          if (referenceValue) {
            referenceValue.textContent = Number(variant.unitReferenceValue) === 1 ? '' : variant.unitReferenceValue;
            referenceValue.hidden = Number(variant.unitReferenceValue) === 1;
          }
          if (referenceUnit) referenceUnit.textContent = variant.unitReferenceUnit || '';
        }
      }
    }

    updateAvailability(variant, announce) {
      const button = this.form?.querySelector('[data-product-submit]');
      const buttonLabel = button?.querySelector('[data-submit-label]');
      const stickyButton = this.root.querySelector('[data-sticky-submit]');
      const status = this.form?.querySelector('[data-product-form-status]');
      const selectionPending = this.requiresPhoneSelection && !this.phoneSelectionConfirmed;
      let label = this.strings.unavailable || '';

      if (selectionPending) {
        label = this.strings.selectPhone || 'Select your phone';
      } else if (variant?.available) {
        label = this.strings.addToCart || label;
      } else if (variant) {
        label = this.strings.soldOut || label;
      }

      const canPurchase = Boolean(variant?.available && !selectionPending);
      if (button) button.disabled = !canPurchase;
      if (stickyButton) {
        stickyButton.disabled = !canPurchase;
        stickyButton.textContent = label;
      }
      if (buttonLabel) buttonLabel.textContent = label;

      if (this.dynamicCheckout) {
        this.dynamicCheckout.hidden = selectionPending;
        if (selectionPending) this.dynamicCheckout.dataset.phoneSelectionHidden = 'true';
      }

      if (status) {
        if (selectionPending) {
          status.textContent = this.strings.selectPhone || 'Select your phone to continue.';
          status.hidden = false;
        } else if (announce) {
          status.textContent = variant?.available ? (this.strings.compatible || '') : label;
          status.hidden = !status.textContent;
        } else if (status.textContent === this.strings.selectPhone) {
          status.textContent = '';
          status.hidden = true;
        }
      }
    }

    updateSetup(options, variant) {
      const phone = this.phoneOptionPosition > 0 ? (options[this.phoneOptionPosition - 1] || '') : '';
      const phoneOutput = this.root.querySelector('[data-setup-phone]');
      const stickyOption = this.root.querySelector('[data-sticky-option]');
      const compatibility = this.root.querySelector('[data-compatibility-status]');
      if (phoneOutput) phoneOutput.textContent = phone;
      if (stickyOption) stickyOption.textContent = phone;
      if (compatibility) {
        compatibility.hidden = !variant?.available || (this.requiresPhoneSelection && !this.phoneSelectionConfirmed);
      }
    }

    updateMediaForVariant(variant) {
      if (!variant?.featuredMediaId) return;
      this.showMedia(String(variant.featuredMediaId), false);
    }

    showMedia(mediaId, focusStage) {
      let target = this.mediaCarousel?.selectByMediaId(mediaId, {
        focusStage,
        announce: Boolean(focusStage)
      });

      if (!target) {
        target = [...this.root.querySelectorAll('[data-media-item]')]
          .find((item) => item.dataset.mediaId === String(mediaId));
      }
      if (!target) return;

      const stickyImage = this.root.querySelector('.sticky-add-to-cart__image');
      if (stickyImage && target.dataset.mediaPreviewSrc) {
        stickyImage.src = target.dataset.mediaPreviewSrc;
        stickyImage.removeAttribute('srcset');
      }
    }

    updateSku(variant) {
      const wrapper = this.root.querySelector('[data-product-sku]');
      const value = this.root.querySelector('[data-product-sku-value]');
      if (!wrapper || !value) return;
      value.textContent = variant?.sku || '';
      wrapper.classList.toggle('is-empty', !variant?.sku);
    }

    destroy() {
      this.phoneSelector?.destroy();
      this.submitObserver?.disconnect();
    }
  }

  const initProductRoots = (scope = document) => {
    collect(scope, '[data-product-root]').forEach((root) => {
      if (initializedRoots.has(root)) return;
      initializedRoots.add(root);
      const controller = new ProductController(root);
      productControllers.set(root, controller);
    });

    collect(scope, '[data-card-media-carousel]').forEach((carousel) => {
      if (initializedCardCarousels.has(carousel)) return;
      initializedCardCarousels.add(carousel);
      new ProductCardCarousel(carousel);
    });

    collect(scope, '[data-product-recommendations]').forEach((section) => {
      if (section.dataset.recommendationsLoaded === 'true' || !section.dataset.recommendationsUrl) return;
      section.dataset.recommendationsLoaded = 'true';
      fetch(section.dataset.recommendationsUrl)
        .then((response) => response.ok ? response.text() : Promise.reject(new Error('Recommendations unavailable')))
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const replacement = parsed.querySelector('[data-product-recommendations]');
          if (replacement?.innerHTML.trim()) {
            section.innerHTML = replacement.innerHTML;
            section.hidden = replacement.hidden;
            initProductRoots(section);
            window.Steezlab?.initialize?.(section);
          }
        })
        .catch(() => {
          section.hidden = true;
        });
    });
  };

  window.SteezlabProduct = { init: initProductRoots };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProductRoots(), { once: true });
  } else {
    initProductRoots();
  }

  document.addEventListener('shopify:section:load', (event) => initProductRoots(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    collect(event.target, '[data-product-root]').forEach((root) => {
      productControllers.get(root)?.destroy();
      productControllers.delete(root);
    });
  });
})();
